import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

const BRAND_BLUE = '#2558ab';
const getCleanValue = (val) => val && val.toString().trim() !== '' ? val.toString() : 'Not yet provided';

export const exportToPDF = (moduleData) => {
  try {
    const doc = new jsPDF();
    let yPos = 20;
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(37, 88, 171); // BRAND_BLUE
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('Planned with Purpose', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('A Gospel-Centered Planning Workspace', pageWidth / 2, 30, { align: 'center' });
    yPos = 55;

    const addSection = (title, content) => {
      if (yPos > 240) { doc.addPage(); yPos = 20; }
      doc.setTextColor(BRAND_BLUE);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(title, 20, yPos);
      yPos += 10;
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      content.forEach(item => {
        const splitText = doc.splitTextToSize(`${item.label}: ${getCleanValue(item.value)}`, 170);
        doc.text(splitText, 25, yPos);
        yPos += (splitText.length * 5) + 3;
        if (yPos > 270) { doc.addPage(); yPos = 20; }
      });
      yPos += 5;
    };

    // Module 1
    if (moduleData.module1?.data) {
      const d = moduleData.module1.data;
      addSection('1. Clarify the Calling', [
        { label: 'Clarity Statement', value: d.clarityStatement },
        { label: 'Biblical Anchor', value: d.biblicalAnchor }
      ]);
    }

    // Module 2 (Updated)
    if (moduleData.module2?.data) {
      const d = moduleData.module2.data;
      const isFiction = d.bookType === 'fiction';
      const fields = [];
      
      if (isFiction) {
        Object.keys(d.fictionData || {}).forEach(key => {
          fields.push({ label: key.toUpperCase(), value: d.fictionData[key].content });
          if (d.fictionData[key].notes) fields.push({ label: `${key.toUpperCase()} Notes`, value: d.fictionData[key].notes });
        });
      } else {
        (d.nonfictionChapters || []).forEach(ch => {
          fields.push({ label: `CH ${ch.chapter} - ${ch.bigIdea}`, value: `Shift: ${ch.readerShift} | Truth: ${ch.biblicalTruth}` });
        });
      }
      addSection('2. Book Structure', fields);
    }

    // Module 3
    if (moduleData.module3?.data) {
      const d = moduleData.module3.data;
      addSection('3. Build Your Book Plan', [
        { label: 'Project Scope', value: d.projectScope },
        { label: 'Target', value: `${d.targetWordCount} words` },
        { label: 'Timeline', value: `${d.idealStartDate} to ${d.idealFinishDate}` }
      ]);
    }

    // Module 4
    if (moduleData.module4?.data) {
      const d = moduleData.module4.data;
      addSection('4. Sustain Momentum', [
        { label: 'Rhythm', value: `${d.bestWritingTimes} at ${d.bestWritingLocations}` },
        { label: 'Reframe Statement', value: d.reframeStatement }
      ]);
    }

    // Module 5
    if (moduleData.module5?.data) {
      const d = moduleData.module5.data;
      const isFiction = d.bookType === 'fiction';
      const fields = isFiction ? [
        { label: 'Type', value: 'Fiction' },
        { label: 'Character', value: d.fictionData?.mainCharacter },
        { label: 'Opening Paragraph', value: d.fictionData?.openingParagraph }
      ] : [
        { label: 'Type', value: 'Nonfiction' },
        { label: 'Hook', value: d.nonfictionData?.hook },
        { label: 'Opening Paragraph', value: d.nonfictionData?.openingParagraph }
      ];
      fields.push({ label: 'Sprint', value: `${d.commitment?.date} @ ${d.commitment?.time}` });
      addSection('5. First Chapter', fields);
    }

    doc.save('Planned-with-Purpose-Workbook.pdf');
  } catch (error) {
    console.error('PDF Generation failed:', error);
  }
};

export const exportToWord = async (moduleData) => {
  try {
    const sections = [];
    const createModulePara = (title, fields) => {
      const paras = [
        new Paragraph({ text: title, heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } })
      ];
      fields.forEach(f => {
        paras.push(new Paragraph({
          children: [
            new TextRun({ text: `${f.label}: `, bold: true }),
            new TextRun({ text: getCleanValue(f.value) })
          ],
          spacing: { after: 120 }
        }));
      });
      return paras;
    };

    if (moduleData.module1?.data) {
      sections.push(...createModulePara('1. Clarify the Calling', [
        { label: 'Clarity Statement', value: moduleData.module1.data.clarityStatement }
      ]));
    }

    if (moduleData.module2?.data) {
      const d = moduleData.module2.data;
      const fields = d.bookType === 'fiction' 
        ? Object.keys(d.fictionData).map(k => ({ label: k.toUpperCase(), value: d.fictionData[k].content }))
        : d.nonfictionChapters.map(ch => ({ label: `Chapter ${ch.chapter}`, value: ch.bigIdea }));
      sections.push(...createModulePara('2. Book Structure', fields));
    }

    const doc = new Document({
      sections: [{
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: "Planned with Purpose", bold: true, size: 48, color: BRAND_BLUE.replace('#', '') })],
            spacing: { after: 400 }
          }),
          ...sections
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "Planned-with-Purpose-Workbook.docx");
  } catch (error) {
    console.error('Word Generation failed:', error);
  }
};

export const sendEmail = (moduleData) => {
  const subject = encodeURIComponent("My Planned with Purpose Workbook Summary");
  const m1 = moduleData.module1?.data?.clarityStatement || "Not yet defined";
  const body = encodeURIComponent(
    `PLANNED WITH PURPOSE WORKBOOK SUMMARY\n` +
    `--------------------------------------\n\n` +
    `MY CLARITY STATEMENT:\n"${m1}"\n\n` +
    `Generated via GospelWise Author Workspace.`
  );
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};